import { createSwiper } from './swiper';

const loadHeroImages = (heroCarousel) => {
  const images = heroCarousel.querySelectorAll('.image, .image-svg__elem');

  images.forEach((img) => {
    if (img.dataset.loaded === 'true') return;
    if (window.lazyImageFn) window.lazyImageFn(img);
  });
};

const pauseAllHeroVideos = (heroCarousel) => {
  heroCarousel.querySelectorAll('.hero__media-video').forEach((v) => {
    if (!v.paused) v.pause();
  });
};

const syncHeroVideos = (heroCarousel) => {
  if (!heroCarousel._heroInView) {
    pauseAllHeroVideos(heroCarousel);
    return;
  }

  heroCarousel.querySelectorAll('.swiper__col').forEach((slide) => {
    const videos = slide.querySelectorAll('.hero__media-video');
    if (!videos.length) return;

    if (slide.classList.contains('swiper-slide-active')) {
      videos.forEach((v) => {
        if (v.paused) {
          const p = v.play();
          if (p) p.catch(() => {});
        }
      });
    } else {
      videos.forEach((v) => {
        if (!v.paused) v.pause();
      });
    }
  });
};

const initHeroCarousel = (heroCarousel) => {
  const heroSection = heroCarousel.closest('.hero');
  const isStyle2 = heroSection?.classList.contains('hero--style-2');
  const paginationEl = heroSection?.querySelector('.hero__pagination');
  const nextEl = heroSection?.querySelector('.hero__nav-btn--next');
  const prevEl = heroSection?.querySelector('.hero__nav-btn--prev');

  if (typeof heroCarousel._heroHoverCleanup === 'function') {
    heroCarousel._heroHoverCleanup();
    heroCarousel._heroHoverCleanup = null;
  }

  if (heroCarousel.swiper && typeof heroCarousel.swiper.destroy === 'function') {
    heroCarousel.swiper.destroy(true, true);
  }

  const autoplay = heroCarousel.dataset.autoplay === 'true';
  const pauseOnHover = heroCarousel.dataset.pauseOnHover === 'true';
  const autoplayDelay = parseInt(heroCarousel.dataset.autoplayDelay, 10) || 5000;
  const speed = parseInt(heroCarousel.dataset.speed, 10) || 500;
  const effect = isStyle2 ? 'slide' : (heroCarousel.dataset.effect || 'fade');
  const isFade = effect === 'fade';

  const swiper = createSwiper(heroCarousel, {
    slidesPerView: 1,
    centeredSlides: false,
    spaceBetween: 0,
    slideClass: 'swiper__col',
    wrapperClass: 'swiper__row',
    effect,
    speed,
    loop: true,
    breakpoints: isStyle2
      ? {
          768: {
            slidesPerView: 1.05,
            centeredSlides: true,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 1.05,
            centeredSlides: true,
            spaceBetween: 20,
          },
        }
      : {},
    allowTouchMove: true,
    noSwipingSelector: '.hero__cta-group, .hero__cta-group a, .hero__cta-group button',
    preventClicks: false,
    preventClicksPropagation: false,
    ...(isFade && {
      fadeEffect: {
        crossFade: true,
      },
    }),
    ...(autoplay && {
      autoplay: {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      },
    }),
    navigation: {
      nextEl,
      prevEl,
    },
    pagination: {
      el: paginationEl,
      clickable: true,
    },
    a11y: {
      enabled: true,
    },
  });

  loadHeroImages(heroCarousel);
  swiper.on('slideChange', () => loadHeroImages(heroCarousel));
  swiper.on('slideChangeTransitionStart', () => loadHeroImages(heroCarousel));

  const hasVideo = heroCarousel.querySelector('.hero__media-video');

  if (autoplay && pauseOnHover) {
    const slideWrapper = heroCarousel.querySelector('.swiper__row');
    const handleMouseEnter = () => {
      heroCarousel.classList.add('is-autoplay-paused');
      if (swiper.autoplay?.running) swiper.autoplay.stop();
    };
    const handleMouseLeave = () => {
      heroCarousel.classList.remove('is-autoplay-paused');
      if (swiper.autoplay && !swiper.autoplay.running && (!hasVideo || heroCarousel._heroInView !== false)) {
        swiper.autoplay.start();
      }
    };

    slideWrapper?.addEventListener('mouseenter', handleMouseEnter);
    slideWrapper?.addEventListener('mouseleave', handleMouseLeave);

    heroCarousel._heroHoverCleanup = () => {
      slideWrapper?.removeEventListener('mouseenter', handleMouseEnter);
      slideWrapper?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }

  if (hasVideo) {
    heroCarousel._heroInView = false;
    pauseAllHeroVideos(heroCarousel);

    swiper.on('slideChange', () => syncHeroVideos(heroCarousel));
    swiper.on('slideChangeTransitionEnd', () => syncHeroVideos(heroCarousel));

    const observer = new IntersectionObserver(
      ([entry]) => {
        heroCarousel._heroInView = entry.isIntersecting;
        if (entry.isIntersecting) {
          heroCarousel.classList.remove('is-autoplay-paused');
          syncHeroVideos(heroCarousel);
          if (autoplay && swiper.autoplay && !swiper.autoplay.running) {
            swiper.autoplay.start();
          }
        } else {
          heroCarousel.classList.add('is-autoplay-paused');
          pauseAllHeroVideos(heroCarousel);
          if (swiper.autoplay?.running) swiper.autoplay.stop();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(heroSection || heroCarousel);
    heroCarousel._heroObserver = observer;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        heroCarousel.classList.add('is-autoplay-paused');
        pauseAllHeroVideos(heroCarousel);
      } else if (heroCarousel._heroInView) {
        heroCarousel.classList.remove('is-autoplay-paused');
        syncHeroVideos(heroCarousel);
      }
    });
  }

  return swiper;
};

const initAllHeroCarousels = () => {
  const heroCarousels = document.querySelectorAll('.hero__carousel');
  return Array.from(heroCarousels).map(initHeroCarousel);
};

const hero = () => {
  initAllHeroCarousels();

  document.addEventListener('shopify:section:load', (e) => {
    const carousel = e.target.querySelector('.hero__carousel');
    if (carousel) initHeroCarousel(carousel);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const carousel = e.target.querySelector('.hero__carousel');
    if (carousel?._heroObserver) {
      carousel._heroObserver.disconnect();
      carousel._heroObserver = null;
    }
    if (typeof carousel?._heroHoverCleanup === 'function') {
      carousel._heroHoverCleanup();
      carousel._heroHoverCleanup = null;
    }
    if (carousel?.swiper) carousel.swiper.destroy(true, true);
  });

  document.addEventListener('shopify:block:select', (e) => {
    const carousel = e.target.closest('.hero__carousel');
    if (!carousel?.swiper) return;
    const slide = e.target.closest('.swiper__col');
    if (!slide) return;
    const index = Array.from(carousel.querySelectorAll('.swiper__col')).indexOf(slide);
    if (index >= 0) {
      carousel.swiper.slideToLoop(index);
      if (carousel.swiper.autoplay?.running) carousel.swiper.autoplay.stop();
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const carousel = e.target.closest('.hero__carousel');
    if (!carousel?.swiper) return;
    if (carousel.dataset.autoplay === 'true' && !carousel.swiper.autoplay?.running) {
      carousel.swiper.autoplay.start();
    }
  });
};

export default hero;
